import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { MxTabBar } from "@/components/shared/MxTabBar";
import { Thumbnail } from "@/components/shared/Thumbnail";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Search, AlertTriangle, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { useAssets } from "@/hooks/useAssets";
import { useFacilities } from "@/hooks/useFacilities";
import { useAllZonesFlat } from "@/hooks/useFacilityZones";
import { CreateAssetDialog } from "@/components/assets/CreateAssetDialog";
import { AssetDetailSheet } from "@/components/assets/AssetDetailSheet";
import { AssetsExportDialog } from "@/components/shared/ExportDialog";
import type { Asset, AssetStatus, AssetType } from "@/hooks/useAssets";

const STATUS_COLORS: Record<string, string> = {
  online: "bg-green-100 text-green-800",
  offline: "bg-gray-100 text-gray-800",
  maintenance: "bg-yellow-100 text-yellow-800",
  decommissioned: "bg-red-100 text-red-800",
};

const PAGE_SIZE = 50;

export default function Assets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get("q") ?? "");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [facilityFilter, setFacilityFilter] = useState<string>("all");
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [page, setPage] = useState(0);

  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
    if (debounceTimer) clearTimeout(debounceTimer);
    setDebounceTimer(setTimeout(() => {
      setDebouncedSearch(value);
      setSearchParams(value ? { q: value } : {}, { replace: true });
    }, 300));
  };

  const zones = useAllZonesFlat();

  const { data: assets = [], isLoading } = useAssets({
    search: debouncedSearch || undefined,
    status: statusFilter !== "all" ? statusFilter as AssetStatus : undefined,
    asset_type: typeFilter !== "all" ? typeFilter as AssetType : undefined,
    facility_id: facilityFilter !== "all" ? facilityFilter : undefined,
  });
  const { data: facilities = [] } = useFacilities();

  const totalPages = Math.ceil(assets.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const paginatedAssets = assets.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const goToPage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="flex flex-col min-h-screen">
      <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Assets</h1>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setExportOpen(true)}>
            <Download className="h-4 w-4" />
            Export
          </Button>
        <CreateAssetDialog />
      </header>
      <MxTabBar />
      <AssetsExportDialog open={exportOpen} onOpenChange={setExportOpen} />

      <div className="flex-1 space-y-4">
        <div className="sticky top-[106px] z-10 flex items-center gap-3 flex-wrap bg-background border-b px-6 py-3">
          <div className="relative w-72">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search assets..." className="pl-8" value={searchTerm} onChange={(e) => handleSearchChange(e.target.value)} />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="decommissioned">Decommissioned</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="bottling">Bottling</SelectItem>
              <SelectItem value="infrastructure">Infrastructure</SelectItem>
              <SelectItem value="safety">Safety</SelectItem>
              <SelectItem value="wine_production">Wine Production</SelectItem>
              <SelectItem value="vehicle">Vehicle</SelectItem>
              <SelectItem value="tools">Tools</SelectItem>
              <SelectItem value="fluids">Fluids</SelectItem>
              <SelectItem value="tank">Tank</SelectItem>
            </SelectContent>
          </Select>
          <Select value={facilityFilter} onValueChange={(v) => { setFacilityFilter(v); setPage(0); }}>
            <SelectTrigger className="w-40"><SelectValue placeholder="Facility" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Facilities</SelectItem>
              {facilities.map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="px-6 pb-6 space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : assets.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {debouncedSearch ? "No assets match your search" : "No assets yet"}
            </p>
            {!debouncedSearch && <CreateAssetDialog />}
          </Card>
        ) : (
          <>
            <div className="border rounded-lg bg-card">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b">
                      <TableHead className="bg-muted w-12"></TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Name</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Type</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Status</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Criticality</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Location</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Make / Model</TableHead>
                      <TableHead className="bg-muted font-semibold text-foreground">Serial #</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedAssets.map((asset) => (
                      <TableRow
                        key={asset.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => { setSelectedAsset(asset); setSheetOpen(true); }}
                      >
                        <TableCell className="p-2">
                          <Thumbnail url={asset.image_url} size={72} className="h-9 w-9 rounded object-cover border border-border" />
                        </TableCell>
                        <TableCell className="py-2 font-medium max-w-[200px] truncate">{asset.name}</TableCell>
                        <TableCell className="py-2">
                          {asset.asset_type ? (
                            <Badge variant="outline" className="text-[10px] whitespace-nowrap">{asset.asset_type.replace("_", " ")}</Badge>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge className={`text-[10px] ${STATUS_COLORS[asset.status] || ""}`}>{asset.status}</Badge>
                        </TableCell>
                        <TableCell className="py-2">
                          {asset.criticality === "critical" ? (
                            <Badge variant="destructive" className="text-[10px] gap-1">
                              <AlertTriangle className="h-3 w-3" />Critical
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">Normal</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground text-sm max-w-[160px] truncate">
                          {asset.zone_id ? (zones.find((z) => z.id === asset.zone_id)?.label ?? "—") : "—"}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground max-w-[160px] truncate">
                          {[asset.make, asset.model].filter(Boolean).join(" ") || "—"}
                        </TableCell>
                        <TableCell className="py-2 text-muted-foreground font-mono text-xs">
                          {asset.serial_number || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, assets.length)} of {assets.length} asset{assets.length !== 1 ? "s" : ""}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={safePage === 0} onClick={() => goToPage(safePage - 1)}>
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </Button>
                  <span className="text-xs text-muted-foreground px-2">
                    Page {safePage + 1} of {totalPages}
                  </span>
                  <Button variant="outline" size="icon" className="h-7 w-7" disabled={safePage >= totalPages - 1} onClick={() => goToPage(safePage + 1)}>
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
        </div>
      </div>

      <AssetDetailSheet
        asset={selectedAsset}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onAssetUpdated={setSelectedAsset}
      />
    </div>
  );
}
